import React from 'react';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import { useThemeContext } from '../../ThemeContext';
import { css } from '@emotion/react';
import { Portal } from '$/components/Portal';
import { ClearIcon } from '$/images/Icons';
import { SearchIcon } from '$/images/FontAwesomeIcons';

export const SearchInputFilter: React.FC<{
  inputRef?: React.RefObject<HTMLInputElement>;
  inputPortalId?: string;
  placeholder?: string;
  value: string;
  isInputOpen: boolean;
  filters?: { query?: string };
  onChange: (query: string) => void;
  onIconClick?: (open?: boolean) => void;
  setFilters?: (filters: Record<string, string | string[]>) => void;
  className?: string;
}> = ({
  className,
  inputRef,
  inputPortalId,
  placeholder,
  value,
  isInputOpen: shouldOpen,
  onChange,
  onIconClick,
  filters = {},
  setFilters,
}) => {
  const isInputOpen = shouldOpen || filters.query;
  const { md: isMd } = useWindowSizeInfo();
  const etheme = useThemeContext();

  const onClick = (open?: boolean): void => {
    onIconClick?.(open);
    setTimeout(() => {
      const newState = open !== undefined ? open : !isInputOpen;
      if (newState) {
        inputRef?.current?.focus();
      }
    }, 200);
  };

  const searchIcon = (
    <SearchIcon
      className='search-icon ml-1/2e md:ml-0 -mt-2 md:mt-0 w-1_125e h-1_125e flex-shrink-0 cursor-pointer duration-200 transition-colors'
      onClick={(): void => onClick()}
      alt='search icon'
      onMouseDown={(e: React.MouseEvent): void => {
        e.preventDefault();
      }}
    />
  );

  const searchBar = (
    <div
      className={`
        ${isInputOpen && 'open'}
        flex items-center pl-3/4e pr-1/4e mt-1/2e md:-mt-2 ml-0 xl:ml-auto duration-200
      `}
      css={css`
        border-radius: 0.55rem;
        padding-top: 4px;
        padding-bottom: 4px;
        background: transparent;
        border: 1px solid transparent;

        transition-property: border-color, background-color;
        .search-icon {
          color: ${etheme.home.filters.control.label.fgIcon};
        }

        max-width: 40px;
        border-color: transparent;
        transition:
          max-width 0.25s,
          border-color 0.25s;
        overflow: hidden;

        &.open {
          display: flex;
          border-color: ${etheme.home.filters.darkControl.bg};
          background: #fff;
          max-width: 999px;
          input {
            opacity: 1;
            width: 100%;
            height: ${isMd ? 'auto' : '2rem'};
          }
          .search-icon {
            margin: 0 0.25rem;
            color: ${etheme.home.filters.darkControl.bg};
          }
        }
      `}
    >
      <input
        ref={inputRef}
        className={` search-form__input text-md font-normal pl-1/2e md:pl-0 md:ml-1/2e outline-none duration-200`}
        css={css`
          line-height: 22px;
          letter-spacing: -0.09px;
          background-color: transparent;
          transition-property: width, opacity;
          opacity: 0;
          width: 0;
          height: 0;
        `}
        placeholder={placeholder ?? 'Search webinars'}
        value={value}
        onChange={(e): void => {
          onChange(e.target.value);
        }}
        onBlur={(): void => {
          if (!filters.query) {
            onClick(false);
          }
        }}
      />
      <div
        className={`cursor-pointer mx-1 md:mx-0  ${!isInputOpen ? 'hidden' : ''} ${
          filters.query ? 'block' : 'hidden'
        }`}
        css={css`
          color: ${etheme.home.filters.darkControl.bg};
        `}
        title='Clear filters'
        onClick={(): void => {
          setFilters?.({ ...filters, query: '' });
          onChange('');
          inputRef?.current?.focus();
        }}
      >
        <ClearIcon className='w-1_25e h-1_25e' />
      </div>
      <div className='ml-auto'>{!inputPortalId && !filters.query ? searchIcon : null}</div>
    </div>
  );

  return (
    <div
      className={className}
      css={css`
        padding-top: 4px;
        .search-icon {
          color: ${etheme.home.filters.control.label.fgIcon};
        }
      `}
    >
      {inputPortalId ? searchIcon : null}
      {inputPortalId ? <Portal containerId={inputPortalId}>{searchBar}</Portal> : searchBar}
    </div>
  );
};
